import clsx from 'clsx'
import { type ReactElement, useEffect, useState } from 'react'
import { submitAlignments, submitWrapper } from '~/components/Form/Form.css'
import {
  Button,
  type ButtonProps,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { SubmitAlignment } from './SubmitAlignment'

interface SubmitProps extends ButtonProps {
  loadingText?: string
  successText?: string
  errorText?: string
  onSuccessReset?: boolean
  globalError?: null | string
  globalErrorInSubmit?: boolean
  isSubmitted?: boolean
  isSubmitting?: boolean
  isSubmitSuccessful?: boolean
  buttonVariation?: ButtonVariation
  submitAlignment?: SubmitAlignment
}

export const Submit = ({
  children,
  onSuccessReset = false,
  globalError,
  globalErrorInSubmit,
  loadingText,
  successText,
  errorText = 'Une erreur est survenue',
  isSubmitted,
  isSubmitting,
  isSubmitSuccessful,
  buttonVariation = ButtonVariation.Primary,
  submitAlignment = SubmitAlignment.None,
  ...rest
}: SubmitProps): ReactElement => {
  const hasGlobalError =
    globalError !== '' && globalError !== undefined && globalError !== null
  if (children === undefined || children === null) {
    throw new Error('Submit must have children')
  }
  if (errorText === undefined) {
    throw new Error('Submit must have an error text')
  }
  if (isSubmitting === undefined || isSubmitSuccessful === undefined) {
    throw new Error('Submit must be in a form')
  }
  const [onError, setOnError] = useState<boolean>(false)
  const [successExpired, setSuccessExpired] = useState(false)

  let submitText = children
  if (isSubmitting) {
    submitText = loadingText !== undefined ? loadingText : 'Chargement...'
  } else if (onError) {
    submitText =
      globalErrorInSubmit === true ? (globalError ?? errorText) : errorText
  } else if (isSubmitSuccessful && !hasGlobalError && !successExpired) {
    submitText = successText !== undefined ? successText : 'Enregistré'
  }

  useEffect(() => {
    let timer: number | undefined
    if (
      isSubmitted &&
      !isSubmitting &&
      (!isSubmitSuccessful || hasGlobalError)
    ) {
      setOnError(true)
      timer = window.setTimeout(() => {
        setOnError(false)
      }, 3000)
    }
    return () => {
      timer && window.clearTimeout(timer)
    }
  }, [isSubmitSuccessful, isSubmitting, isSubmitted, hasGlobalError])

  useEffect(() => {
    if (isSubmitting) {
      setSuccessExpired(false)
      return
    }
    if (!isSubmitSuccessful || hasGlobalError || !onSuccessReset) {
      setSuccessExpired(false)
      return
    }
    const timer = window.setTimeout(() => {
      setSuccessExpired(true)
    }, 2000)
    return () => window.clearTimeout(timer)
  }, [isSubmitSuccessful, isSubmitting, hasGlobalError, onSuccessReset])

  const isDisabled: boolean =
    isSubmitting ||
    (isSubmitSuccessful &&
      !hasGlobalError &&
      !onSuccessReset &&
      !successExpired)
  const buttonStyle: ButtonVariation = onError
    ? ButtonVariation.Danger
    : isSubmitSuccessful && !hasGlobalError && !successExpired
      ? ButtonVariation.Secondary
      : buttonVariation

  return (
    <div
      className={clsx(
        submitAlignment !== SubmitAlignment.None
          ? submitAlignments[submitAlignment]
          : undefined,
        submitWrapper
      )}
    >
      <Button
        type="submit"
        disabled={isDisabled}
        variation={buttonStyle}
        {...rest}
      >
        {submitText}
      </Button>
    </div>
  )
}

Submit.displayName = 'Submit'
